// Libs
import classNames from 'classnames/bind';
import { Box } from '@mui/material';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
// Components, Layouts, Pages
import { BaseMoreAction, BaseStatus, BaseTable, BaseToolbar, Loading } from '~/components/common';
// Others
import { IGlobal } from '~/utils/interface/global';
import { StorageEnum } from '~/utils/enum/base';
import { getListGlobal } from '~/thunks/global/globalThunk';
import { globalActions } from '~/thunks/global/globalSlice';
import { usePermissions } from '~/utils/hooks/userPermissions';
import { IPaginationResponse } from '~/utils/interface/base';
import { formatCurrencyUSD, formatDate } from '~/utils/helper/common';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { ORDER_PERMISSIONS, QUOTE_PERMISSIONS } from '~/utils/constants/permissions';
import { BusinessTypeEnum, DateFormatEnum, RoleEnum } from '~/utils/enum/common';
import { IBaseMoreAction, IHeaderCellType, IQueryParamBase } from '~/utils/interface/common';
import { DEFAULT_CURRENT_PAGE, DEFAULT_LIMIT_PAGE, EMPTY_STRING } from '~/utils/constants/common';
import { crewRouteAbsolute } from '~/utils/routes/roleRoutes/crew';
import { adminRouteAbsolute } from '~/utils/routes/roleRoutes/admin';
import { loaderRouteAbsolute } from '~/utils/routes/roleRoutes/loader';
import { foremanRouteAbsolute } from '~/utils/routes/roleRoutes/foreman';
import { salesAdminRouteAbsolute } from '~/utils/routes/roleRoutes/salesAdmin';
import { salesPersonRouteAbsolute } from '~/utils/routes/roleRoutes/salesPerson';
import { warehouseLeadRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseLead';
import { warehouseStaffRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseStaff';
import { operationsManagerRouteAbsolute } from '~/utils/routes/roleRoutes/operationsManager';
// Styles, images, icons
import styles from './Global.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const columnsGlobals = (
  t: TFunction,
  onView: (data: IGlobal) => void,
  isDetail?: boolean
): IHeaderCellType<IGlobal>[] => {
  return [
    {
      id: 'id',
      label: t('table_header_title_id'),
      render: (record: IGlobal) => record?.number,
      minWidth: 120,
    },
    {
      id: 'eventName',
      label: t('table_header_title_event_name'),
      render: (record: IGlobal) => record.eventTitle,
      minWidth: 140,
    },
    {
      id: 'type',
      label: t('table_header_title_type'),
      render: (record: IGlobal) => <BaseStatus label={record?.type || EMPTY_STRING} />,
      minWidth: 100,
    },
    {
      id: 'startDate',
      label: t('table_header_title_start_date'),
      render: (record: IGlobal) => formatDate(record?.startDate, DateFormatEnum.M_D_YYYY),
      minWidth: 140,
    },
    {
      id: 'endDate',
      label: t('table_header_title_end_date'),
      render: (record: IGlobal) => formatDate(record.endDate, DateFormatEnum.M_D_YYYY),
      minWidth: 140,
    },
    {
      id: 'quoteAmount',
      label: t('table_header_title_quote_amount'),
      render: (record: IGlobal) => formatCurrencyUSD({ value: record?.total, showDecimal: true }),
      minWidth: 160,
    },
    {
      id: 'status',
      label: t('table_header_title_status'),
      render: (record: IGlobal) => <BaseStatus label={record?.status || EMPTY_STRING} />,
      minWidth: 100,
    },
    {
      id: 'action',
      label: t('table_header_title_actions'),
      minWidth: 100,
      align: 'center',
      render: (record: IGlobal) => {
        const actions: IBaseMoreAction[] = [
          ...(isDetail
            ? [
                {
                  label: t('table_action_view', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionView}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  onClick: () => onView(record),
                },
              ]
            : []),
        ];

        return <BaseMoreAction actions={actions} />;
      },
    },
  ];
};

const Global = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  const { canDetailQuotes } = usePermissions(QUOTE_PERMISSIONS);
  const { canDetailOrders } = usePermissions(ORDER_PERMISSIONS);
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/quotes', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [searchParams, setSearchParams] = useSearchParams();
  const searchKey = searchParams.get('searchKey');
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(() => Number(params?.page ?? DEFAULT_CURRENT_PAGE), [params?.page]);
  const limitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);

  const currentRole = JSON.parse(localStorage.getItem(StorageEnum.ROLE)!)[0];
  //#endregion Declare Hook

  //#region Selector
  const { isRefreshGlobalList } = useAppSelector((state) => state.global);
  //#endregion Selector

  //#region Declare State
  const [paramObject, setParamObject] = useState<IQueryParamBase>({
    page: pageSelected,
    limit: limitPage,
    textSearch: searchKey || EMPTY_STRING,
  });
  const [pagination, setPagination] = useState<IPaginationResponse>();

  const [globalList, setGlobalList] = useState<IGlobal[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListGlobal(paramObject);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    if (!isRefreshGlobalList) return;

    handleGetListGlobal(paramObject);

    dispatch(globalActions.setRefreshGlobalList(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshGlobalList]);

  useEffect(() => {
    if (pagination || searchKey) {
      setParamObject({
        ...paramObject,
        page: DEFAULT_CURRENT_PAGE,
        limit: limitPage,
        ...(searchKey ? { textSearch: searchKey } : { textSearch: undefined }),
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchKey]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListGlobal = (params: IQueryParamBase) => {
    setIsLoading(true);

    dispatch(getListGlobal(params))
      .unwrap()
      .then((res) => {
        const { responses, pagination } = res?.data;

        if (!responses) return;

        setGlobalList(responses);
        setPagination(pagination);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handlePageChange = (event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
    setSearchParams({ ...params, page: String(page), limit: String(limitPage) });
  };

  const handleView = (data: IGlobal) => {
    if (data?.type === BusinessTypeEnum.ORDER && canDetailOrders) {
      switch (currentRole) {
        case RoleEnum.SUPER_ADMIN:
          navigate(adminRouteAbsolute.detailOrder(data?.id));
          break;
        case RoleEnum.SALES_ADMIN:
          navigate(salesAdminRouteAbsolute.detailOrder(data?.id));
          break;
        case RoleEnum.SALES_PERSON:
          navigate(salesPersonRouteAbsolute.detailOrder(data?.id));
          break;
        case RoleEnum.OPERATIONS_MANAGER:
          navigate(operationsManagerRouteAbsolute.detailOrder(data?.id));
          break;
        case RoleEnum.WAREHOUSE_LEAD:
          navigate(warehouseLeadRouteAbsolute.detailOrder(data?.id));
          break;
        case RoleEnum.WAREHOUSE_STAFF:
          navigate(warehouseStaffRouteAbsolute.detailOrder(data?.id));
          break;
        case RoleEnum.FOREMAN:
          navigate(foremanRouteAbsolute.detailOrder(data?.id));
          break;
        case RoleEnum.CREW:
          navigate(crewRouteAbsolute.detailOrder(data?.id));
          break;
        case RoleEnum.LOADER:
          navigate(loaderRouteAbsolute.detailOrder(data?.id));
          break;
      }
    }

    if (data?.type === BusinessTypeEnum.QUOTE && canDetailQuotes) {
      switch (currentRole) {
        case RoleEnum.SUPER_ADMIN:
          navigate(adminRouteAbsolute.detailQuote(data?.id));
          break;
        case RoleEnum.SALES_ADMIN:
          navigate(salesAdminRouteAbsolute.detailQuote(data?.id));
          break;
        case RoleEnum.SALES_PERSON:
          navigate(salesPersonRouteAbsolute.detailQuote(data?.id));
          break;
        case RoleEnum.OPERATIONS_MANAGER:
          navigate(operationsManagerRouteAbsolute.detailQuote(data?.id));
          break;
        case RoleEnum.WAREHOUSE_LEAD:
          navigate(warehouseLeadRouteAbsolute.detailQuote(data?.id));
          break;
        case RoleEnum.WAREHOUSE_STAFF:
          navigate(warehouseStaffRouteAbsolute.detailQuote(data?.id));
          break;
        case RoleEnum.FOREMAN:
          navigate(foremanRouteAbsolute.detailQuote(data?.id));
          break;
        case RoleEnum.CREW:
          navigate(crewRouteAbsolute.detailQuote(data?.id));
          break;
        case RoleEnum.LOADER:
          navigate(loaderRouteAbsolute.detailQuote(data?.id));
          break;
      }
    }
  };
  //#endregion Handle Function

  return (
    <Box id='globalPage' className={cx('container')}>
      <BaseToolbar
        title={t('quotes_title_search_globals', {
          value: searchKey,
        })}
      />

      <Box className={cx('body')}>
        <BaseTable
          dataSource={globalList}
          currentPage={pageSelected}
          totalPage={pagination?.totalPages}
          columns={columnsGlobals(t, handleView, canDetailQuotes || canDetailOrders)}
          onPageChange={handlePageChange}
          onRowClick={(_, data) => handleView(data)}
        />
      </Box>

      {isLoading && <Loading />}
    </Box>
  );
};

export default Global;
